// src/services/customer-map-api.ts
// 顧客地図API。参考(ref/map)の CustomerMapRestController を現プロジェクトの
// fetchWithTokenRefresh 作法に合わせて移植したもの。
// サーバーが up-tone 用に /api2/map/* として実装（他の api2 と同じ Bearer 認証）。
//   /api2/map/init   → customerMapAPI.init
//   /api2/map/select → customerMapAPI.select
//   /api2/map/repage → customerMapAPI.repage

import { CUSTOMER_MAP_INIT_API, CUSTOMER_MAP_SELECT_API, CUSTOMER_MAP_REPAGE_API } from '../config';
import { fetchWithTokenRefresh } from './token-manager';
import { logApiError } from '../utils/errorLogger';
import {
  CustomerMapInitResponse,
  CustomerMapRepageRequest,
  CustomerMapRepageResponse,
  CustomerMapSelectResponse,
} from '../types/customerMap';

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/** エラー時の共通戻り値。 */
const errorResult = (error: unknown) => ({
  statuscode: 400,
  message: error instanceof Error ? error.message : 'サーバーとの通信に失敗しました',
});

/**
 * api2 のレスポンス封筒を剥がして平坦化する。
 * /api2/map/* は地図データを `mapdata`（詳細APIの `customerdata` と同じ流儀）で包んで返すため、
 * 中身を最上位へ展開し、hook/型はリファレンスDTOと同じ平坦な形で扱えるようにする。
 * statuscode / message / errorMessageList は封筒の外側にあるので維持する。
 */
const unwrap = (data: any): any => {
  if (!data || typeof data !== 'object') return data;
  const envelope = data.mapdata ?? data.customerdata;
  if (envelope && typeof envelope === 'object') {
    return {
      statuscode: data.statuscode,
      message: data.message,
      errorMessageList: data.errorMessageList,
      ...envelope,
    };
  }
  return data;
};

/**
 * レスポンスをJSONとして読む。/api2/map/* は他の api2 と同じく Bearer 認証
 * （fetchWithTokenRefresh がトークン付与・期限切れ時の更新を担う）。
 * HTTPエラーは本文を添えて投げ、呼び出し元の各メソッドが errorResult へ変換する。
 * 正常時は封筒(mapdata)を剥がして返す。
 */
const readJson = async (response: Response): Promise<any> => {
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(`地図APIエラー: ${response.status} ${response.statusText} ${errorText}`);
  }
  return unwrap(await response.json());
};

export const customerMapAPI = {
  /**
   * 初期化（選択顧客・地図設定・マスター取得）。ref: CustomerMapRestController#init
   * @param customerno 選択顧客No（未選択なら null/undefined）
   */
  init: async (customerno?: number | null): Promise<CustomerMapInitResponse> => {
    try {
      const response = await fetchWithTokenRefresh(CUSTOMER_MAP_INIT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ customerno: customerno ?? null }),
      });
      return await readJson(response);
    } catch (error) {
      logApiError('customerMapAPI.init', error, CUSTOMER_MAP_INIT_API, 'POST');
      return errorResult(error) as CustomerMapInitResponse;
    }
  },

  /**
   * 顧客取得（顧客Noから詳細取得）。ref: CustomerMapRestController#select
   * @param customerno 顧客No
   */
  select: async (customerno: number): Promise<CustomerMapSelectResponse> => {
    try {
      const response = await fetchWithTokenRefresh(CUSTOMER_MAP_SELECT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ customerno }),
      });
      return await readJson(response);
    } catch (error) {
      logApiError('customerMapAPI.select', error, CUSTOMER_MAP_SELECT_API, 'POST');
      return errorResult(error) as CustomerMapSelectResponse;
    }
  },

  /**
   * 顧客再描画（表示領域内の一覧・周辺顧客取得）。ref: CustomerMapRestController#repage
   * @param params 表示領域・ページング・ソート・クエリー条件
   */
  repage: async (params: CustomerMapRepageRequest): Promise<CustomerMapRepageResponse> => {
    try {
      const response = await fetchWithTokenRefresh(CUSTOMER_MAP_REPAGE_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(params),
      });
      return await readJson(response);
    } catch (error) {
      logApiError('customerMapAPI.repage', error, CUSTOMER_MAP_REPAGE_API, 'POST');
      return errorResult(error) as CustomerMapRepageResponse;
    }
  },
};
